import { KeyboardArrowDownRounded } from "@mui/icons-material";
import { green } from "@mui/material/colors";

import colors from "tailwindcss/colors";

/*
Global styles for the site, handled by Material UI
*/
const theme = {
  spacing: (factor) => `${0.25 * factor}rem`,
  palette: {
    primary: {
      light: colors.slate[300],
      main: colors.slate[700],
      dark: colors.slate[900],
      contrastText: colors.slate[100],
    },
    secondary: {
      mute: "#E5F7FF",
      lighter: "#B9EBFF",
      light: "#60C5EA",
      main: "#0099cc",
      dark: "#005685",
      darker: "#003351",
      contrastText: colors.slate[700],
    },
    text: {
      primary: colors.slate[700],
      secondary: colors.slate[500],
    },
    grey: {
      ...colors.slate,
      A100: colors.slate[100],
      A200: colors.slate[200],
      A400: colors.slate[400],
      A700: colors.slate[700],
    },
    action: {
      hover: colors.slate[100],
    },
    success: {
      light: colors.green[200],
      main: colors.green[500],
      dark: colors.green[800],
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        endIcon: {
          "& > *:nth-of-type(1)": {
            fontSize: "1.25em",
          },
        },
        startIcon: {
          "& > *:nth-of-type(1)": {
            fontSize: "1.25em",
          },
        },
        root: {
          borderRadius: "4px",
          textTransform: "none",
          fontWeight: 500,
          letterSpacing: "0.025em",
          transition: "all 0.2s ease-in-out",
          variants: [
            {
              props: { size: "small" },
              style: {
                fontWeight: 400,
              },
            },
            {
              props: { color: "primary", variant: "contained" },
              style: {
                "&:hover": {
                  boxShadow:
                    "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                },
              },
            },
            {
              props: { color: "primary", variant: "outlined" },
              style: {
                "&:hover": {
                  boxShadow:
                    "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                },
              },
            },
            {
              props: { variant: "contained", color: "primary" },
              style: {
                backgroundImage: `linear-gradient(to right, ${colors.slate[800]}, ${colors.slate[700]})`,
                color: colors.white,
                "&:hover": {
                  backgroundImage: `linear-gradient(to right, ${colors.slate[800]}, ${colors.slate[600]})`,
                },
                "&.Mui-disabled": {
                  backgroundColor: colors.slate[500],
                  color: colors.slate[200],
                  backgroundImage: "none",
                },
              },
            },
            {
              props: { variant: "contained", color: "secondary" },
              style: {
                backgroundColor: colors.white,
                color: colors.slate[700],
                "&:hover": {
                  backgroundColor: colors.slate[200],
                },
              },
            },
            {
              props: { variant: "contained", color: "neutral" },
              style: {
                backgroundColor: colors.slate[300],
                color: colors.slate[700],
                "&:hover": {
                  backgroundColor: colors.slate[200],
                },
              },
            },
            {
              props: { variant: "outlined", color: "primary" },
              style: {
                borderColor: colors.slate[300],
                backgroundColor: colors.transparent,
                "&:hover": {
                  backgroundColor: colors.transparent,
                  borderColor: colors.slate[400],
                },
              },
            },
            {
              props: { variant: "outlined", color: "secondary" },
              style: {
                borderColor: colors.slate[700],
                color: colors.slate[700],
                backgroundColor: colors.white,
                "&:hover": {
                  borderColor: colors.slate[800],
                },
              },
            },
            {
              props: { variant: "text", color: "secondary" },
              style: {
                color: "#066586",
              },
            },
            // custom variants -> extend ts type in /types/mui.d.ts
            {
              props: { variant: "danger" },
              style: {
                color: colors.gray[600],
                "&:hover": {
                  color: colors.red[600],
                },
              },
            },
          ],
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          // fa-canvas-square's negative margin keeps inline icons from growing the
          // line box, but in a flex container it shrinks the centered box instead.
          "& .fa-canvas-square": {
            marginBlockEnd: 0,
          },
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          "& > button.MuiButton-root": {
            borderColor: colors.gray[300],
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: colors.slate[500],
          // Matches fa-canvas-square: 1.25em wide (FA default) by 1.25em tall.
          "& .fa-regular, & .fa-solid": {
            paddingBlock: "0.125em",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: "auto",
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          // same as modal default z-index https://mui.com/material-ui/customization/z-index/
          zIndex: 1300,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownRounded,
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { variant: "pill" },
              style: {
                fontSize: "1rem",
                width: "2.625em",
                height: "1.5em",
                padding: 0,

                ".MuiFormControlLabel-root > &": {
                  marginLeft: "12px",
                  marginRight: "12px",
                },
                "& .MuiSwitch-switchBase": {
                  padding: "0.125em",
                  transitionDuration: "200ms",
                  "&.Mui-checked": {
                    transform: "translateX(1.125em)",
                    color: colors.white,
                    "& + .MuiSwitch-track": {
                      backgroundColor: colors.green[600],
                      opacity: 1,
                    },
                  },
                  "&.Mui-disabled": {
                    color: colors.slate[100],
                    "& + .MuiSwitch-track": {
                      backgroundColor: colors.slate[200],
                      opacity: 1,
                    },
                    "&.Mui-checked": {
                      color: colors.white,
                      "& + .MuiSwitch-track": {
                        backgroundColor: colors.green[600],
                        opacity: 0.5,
                      },
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: "1.25em",
                  height: "1.25em",
                  boxShadow: "0 1px 2px rgb(15 23 42 / 0.3)",
                },
                "& .MuiSwitch-track": {
                  borderRadius: "9999px",
                  backgroundColor: colors.slate[300],
                  opacity: 1,
                },
              },
            },
            {
              props: { variant: "pill", size: "small" },
              style: {
                fontSize: "0.8125rem",
              },
            },
          ],
        },
      },
    },
    MuiNativeSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownRounded,
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          "&:hover .MuiTableSortLabel-icon": {
            opacity: 1,
          },
          "&.Mui-active .MuiTableSortLabel-icon": {
            color: colors.slate[700],
          },
        },
        icon: {
          transition: "opacity .2s, color .2s",
          opacity: 0.6,
          color: colors.slate[400],
          fontSize: "14px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          "&.MuiChip-colorSuccess": {
            backgroundColor: green[100],
            color: green[900],
          },
          "&.MuiChip-colorInfo": {
            backgroundColor: colors.blue[200],
            color: colors.blue[950],
          },
          "&.MuiChip-colorError": {
            backgroundColor: colors.red[200],
            color: colors.red[900],
          },
        },
      },
    },
  },
};

export default theme;
